import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  BriefcaseIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  HeartIcon,
  DocumentIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet-async';
import {
  useGetDepartmentsQuery,
  useGetDesignationsQuery,
  useGetEmployeeQuery,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
  useGenerateEmployeeIdQuery,
  useGetUsersQuery
} from '../../services/employeeApi';
import { useGetShiftsQuery } from '../../services/shiftApi';
import { useGetBranchesQuery } from '../../services/branchApi';
import { useGetEmployeeDocumentsQuery } from '../../services/documentApi';
import { 
  useGetDefaultSalaryStructureQuery,
  useCalculateSalaryBreakdownMutation 
} from '../../services/salaryStructureApi';
import DocumentUpload from '../../components/DocumentUpload';
import { generateDummyEmployees, companies } from '../../utils/dummyData';

const EmployeeForm = () => {
  const { t } = useTranslation(['common', 'employee']);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const { currentEmployee } = useSelector(state => state.employee);
  const { user } = useSelector(state => state.auth);

  // API queries for departments and designations
  const { data: departmentsResponse = {}, isLoading: departmentsLoading, error: departmentsError } = useGetDepartmentsQuery();
  const { data: designationsResponse = {}, isLoading: designationsLoading, error: designationsError } = useGetDesignationsQuery();
  const { data: usersResponse = {}, isLoading: usersLoading } = useGetUsersQuery({ page: 1, limit: 100 });
  const { data: shiftsResponse = {}, isLoading: shiftsLoading } = useGetShiftsQuery({ isActive: true });
  const { data: branchesResponse = {}, isLoading: branchesLoading } = useGetBranchesQuery({
    companyId: user?.companyId,
    isActive: true
  });

  // API query for employee documents (only when editing)
  const { data: documentsResponse = {}, isLoading: documentsLoading } = useGetEmployeeDocumentsQuery(
    isEditing ? id : null,
    { skip: !isEditing || !id }
  );

  // Salary Structure API queries
  const { data: defaultStructureResponse = {}, isLoading: structureLoading } = useGetDefaultSalaryStructureQuery();
  const [calculateBreakdown] = useCalculateSalaryBreakdownMutation();

  // API query for fetching employee when editing
  const { data: employeeResponse = null, isLoading: employeeLoading } = useGetEmployeeQuery(
    id,
    { skip: !isEditing || !id }
  );

  // API query for generating employee ID (only for new employees)
  const { 
    data: generatedIdResponse, 
    isLoading: generatingId,
    refetch: refetchEmployeeId 
  } = useGenerateEmployeeIdQuery(
    undefined,
    { skip: isEditing }
  );

  // Extract the actual data arrays from the API responses
  const departments = departmentsResponse.data || [];
  const designations = designationsResponse.data || [];
  const users = usersResponse.data?.users || [];
  const shifts = shiftsResponse.data || [];
  const branches = branchesResponse.data || [];

  console.log('Dropdown data loaded:', {
    departments: departments.length,
    designations: designations.length,
    branches: branches.length,
    shifts: shifts.length,
    users: users.length
  });

  // RTK Query mutations
  const [createEmployeeMutation, { isLoading: isCreating }] = useCreateEmployeeMutation();
  const [updateEmployeeMutation, { isLoading: isUpdating }] = useUpdateEmployeeMutation();

  // Combined loading state
  const loading = departmentsLoading || designationsLoading || shiftsLoading || branchesLoading || (isEditing && documentsLoading) || (isEditing && (employeeLoading || !employeeResponse)) || isCreating || isUpdating;

  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic Information
    employeeId: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',

    // Employment Details
    department: '',
    designation: '',
    branch: '',
    shiftId: '',
    employmentType: 'full-time',
    manager: '',
    workLocation: '',
    hireDate: '',
    joiningDate: '',
    probationPeriod: '3',
    status: 'active',

    // Compensation
    salary: '',
    hourlyRate: '',
    payFrequency: 'monthly',
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    bankBranch: '',
    accountHolderName: '',

    // Tax Information
    taxInfo: {
      pan: '',
      taxRegime: 'new',
      pfNumber: '',
      esiNumber: '',
      uanNumber: '',
      ptApplicable: false,
      tdsApplicable: false,
      pfApplicable: false,
      esicApplicable: false,
      tdsDeclaration: {
        isDeclared: false,
        declarationType: 'no_tds',
        reason: 'no_income',
        remarks: ''
      }
    },

    // TDS Declaration
    tdsDeclaration: {
      isDeclared: false,
      declarationType: 'no_tds',
      reason: 'no_income',
      supportingDocuments: [],
      remarks: ''
    },

    // Address Information
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },

    // Emergency Contact
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },

    // Additional Details
    skills: [],
    qualifications: [],
    certifications: [],
    experience: '',
    previousEmployer: '',
    references: []
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [salaryBreakdown, setSalaryBreakdown] = useState(null);
  const [isCalculatingSalary, setIsCalculatingSalary] = useState(false);
  const [skillInputValue, setSkillInputValue] = useState('');

  // Helper function to format dates for HTML date inputs
  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.warn('Error formatting date:', dateValue, error);
      return '';
    }
  };

  // Function to initialize form with dummy data for testing
  const initializeWithDummyData = () => {
    // Confirm with user before overwriting existing data
    const hasExistingData = formData.firstName || formData.lastName || formData.email;
    if (hasExistingData) {
      const confirmOverwrite = window.confirm(
        'This will replace any existing form data with dummy data. Continue?'
      );
      if (!confirmOverwrite) return;
    }

    const dummyEmployees = generateDummyEmployees(1);
    const dummyEmployee = dummyEmployees[0];

    // Get company info
    const company = companies.find(c => c._id === dummyEmployee.companyId);

    setFormData({
      // Basic Information
      employeeId: dummyEmployee.employeeId,
      firstName: dummyEmployee.firstName,
      lastName: dummyEmployee.lastName,
      email: dummyEmployee.email,
      phone: dummyEmployee.phone,
      dateOfBirth: formatDateForInput(dummyEmployee.dateOfBirth),
      gender: dummyEmployee.gender,
      maritalStatus: dummyEmployee.maritalStatus,

      // Employment Details
      department: dummyEmployee.department,
      designation: dummyEmployee.designation,
      branch: dummyEmployee.branch || '',
      shiftId: dummyEmployee.shiftId || '',
      employmentType: dummyEmployee.employmentType,
      manager: dummyEmployee.manager,
      workLocation: dummyEmployee.workLocation,
      hireDate: formatDateForInput(dummyEmployee.hireDate),
      joiningDate: formatDateForInput(dummyEmployee.joiningDate),
      probationPeriod: dummyEmployee.probationPeriod,
      status: dummyEmployee.status,

      // Compensation
      salary: dummyEmployee.salary.toString(),
      hourlyRate: dummyEmployee.hourlyRate.toString(),
      payFrequency: dummyEmployee.payFrequency,
      bankName: dummyEmployee.bankName,
      accountNumber: dummyEmployee.accountNumber,
      ifscCode: dummyEmployee.ifscCode,
      bankBranch: dummyEmployee.bankBranch || 'Main Branch',
      accountHolderName: dummyEmployee.accountHolderName || dummyEmployee.firstName + ' ' + dummyEmployee.lastName,

      // Tax Information
      taxInfo: {
        pan: dummyEmployee.pan || 'ABCDE1234F',
        taxRegime: dummyEmployee.taxRegime || 'new',
        pfApplicable: dummyEmployee.pfApplicable !== undefined ? dummyEmployee.pfApplicable : false,
        pfNumber: dummyEmployee.pfNumber || 'PF' + Math.random().toString().substr(2, 10),
        esiNumber: dummyEmployee.esiNumber || 'ESI' + Math.random().toString().substr(2, 10),
        uanNumber: dummyEmployee.uanNumber || 'UAN' + Math.random().toString().substr(2, 12),
        ptApplicable: dummyEmployee.ptApplicable !== undefined ? dummyEmployee.ptApplicable : false,
        esicApplicable: dummyEmployee.esicApplicable !== undefined ? dummyEmployee.esicApplicable : false,
        tdsApplicable: dummyEmployee.tdsApplicable !== undefined ? dummyEmployee.tdsApplicable : false,
        tdsDeclaration: {
          isDeclared: dummyEmployee.tdsDeclaration?.isDeclared || true,
          declarationType: dummyEmployee.tdsDeclaration?.declarationType || 'lower_tds',
          reason: dummyEmployee.tdsDeclaration?.reason || 'below_threshold',
          remarks: dummyEmployee.tdsDeclaration?.remarks || 'Employee has income below taxable threshold and is eligible for lower TDS deduction.'
        }
      },

      // TDS Declaration
      tdsDeclaration: {
        isDeclared: dummyEmployee.tdsDeclaration?.isDeclared || true,
        declarationType: dummyEmployee.tdsDeclaration?.declarationType || 'lower_tds',
        reason: dummyEmployee.tdsDeclaration?.reason || 'below_threshold',
        supportingDocuments: dummyEmployee.tdsDeclaration?.supportingDocuments || [],
        remarks: dummyEmployee.tdsDeclaration?.remarks || 'Employee has income below taxable threshold and is eligible for lower TDS deduction.'
      },

      // Address Information
      address: {
        street: dummyEmployee.address.street,
        city: dummyEmployee.address.city,
        state: dummyEmployee.address.state,
        zipCode: dummyEmployee.address.zipCode,
        country: dummyEmployee.address.country
      },

      // Emergency Contact
      emergencyContact: {
        name: dummyEmployee.emergencyContact.name,
        relationship: dummyEmployee.emergencyContact.relationship,
        phone: dummyEmployee.emergencyContact.phone,
        email: dummyEmployee.emergencyContact.email
      },

      // Additional Details
      skills: dummyEmployee.skills || [],
      qualifications: dummyEmployee.qualifications || [],
      certifications: dummyEmployee.certifications || [],
      experience: dummyEmployee.experience,
      previousEmployer: dummyEmployee.previousEmployer,
      references: dummyEmployee.references || []
    });

    // Clear any existing errors
    setErrors({});
    setSuccessMessage(`Form filled with dummy data successfully! Employee ID: ${dummyEmployee.employeeId}. You can now review and submit.`);

    console.log('Form initialized with dummy data:', {
      employeeId: dummyEmployee.employeeId,
      name: `${dummyEmployee.firstName} ${dummyEmployee.lastName}`,
      department: dummyEmployee.department,
      designation: dummyEmployee.designation,
      company: company?.name,
      email: dummyEmployee.email
    });
  };

  // Populate form when employee data is loaded by RTK Query
  useEffect(() => {
    if (!isEditing) return;

    console.log('EmployeeForm: Raw employeeResponse:', employeeResponse);
    console.log('EmployeeForm: Raw currentEmployee:', currentEmployee);

    const source = employeeResponse || (currentEmployee ? { data: { employee: currentEmployee } } : null);
    const emp = source?.data?.employee || source?.employee || null;

    console.log('EmployeeForm: Extracted employee data:', emp);

    if (!emp) {
      console.log('EmployeeForm: No employee data found');
      return;
    }

    setFormData({
      employeeId: emp.employeeId || '',
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      phone: emp.phone || '',
      dateOfBirth: formatDateForInput(emp.dateOfBirth),
      gender: emp.gender || '',
      maritalStatus: emp.maritalStatus || '',

      department: emp.department?._id || emp.department || '',
      designation: emp.designation?._id || emp.designation || '',
      branch: emp.branchId?._id || emp.branchId || emp.branch?._id || emp.branch || '',
      shiftId: emp.shiftId?._id || emp.shiftId || '',
      employmentType: emp.employmentType || 'full-time',
      manager: emp.manager || '',
      workLocation: emp.workLocation || '',
      hireDate: formatDateForInput(emp.hireDate || emp.dateOfJoining),
      joiningDate: formatDateForInput(emp.joiningDate || emp.dateOfJoining),
      probationPeriod: emp.probationPeriod || '3',
      status: emp.status || 'active',

      salary: emp.salary ? (typeof emp.salary === 'object' ? emp.salary.gross : emp.salary) : '',
      hourlyRate: emp.hourlyRate ? String(emp.hourlyRate) : '',
      payFrequency: emp.payFrequency || 'monthly',
      bankName: emp.bankName || emp.bankDetails?.bankName || '',
      accountNumber: emp.accountNumber || emp.bankDetails?.accountNumber || '',
      ifscCode: emp.ifscCode || emp.bankDetails?.ifscCode || '',
      bankBranch: emp.bankBranch || emp.bankDetails?.branchName || '',
      accountHolderName: emp.accountHolderName || emp.bankDetails?.accountHolderName || `${emp.firstName || ''} ${emp.lastName || ''}`.trim(),

      // Tax Information
      taxInfo: {
        pan: emp.taxInfo?.pan || '',
        taxRegime: emp.taxInfo?.taxRegime || 'new',
        pfApplicable: emp.taxInfo?.pfApplicable !== undefined ? emp.taxInfo.pfApplicable : false,
        pfNumber: emp.taxInfo?.pfNumber || '',
        esiNumber: emp.taxInfo?.esiNumber || '',
        uanNumber: emp.taxInfo?.uanNumber || '',
        ptApplicable: emp.taxInfo?.ptApplicable !== undefined ? emp.taxInfo.ptApplicable : false,
        esicApplicable: emp.taxInfo?.esicApplicable !== undefined ? emp.taxInfo.esicApplicable : false,
        tdsApplicable: emp.taxInfo?.tdsApplicable !== undefined ? emp.taxInfo.tdsApplicable : false,
        tdsDeclaration: {
          isDeclared: emp.taxInfo?.tdsDeclaration?.isDeclared || false,
          declarationType: emp.taxInfo?.tdsDeclaration?.declarationType || 'no_tds',
          reason: emp.taxInfo?.tdsDeclaration?.reason || 'no_income',
          remarks: emp.taxInfo?.tdsDeclaration?.remarks || ''
        }
      },

      address: {
        street: emp.address?.street || '',
        city: emp.address?.city || '',
        state: emp.address?.state || '',
        zipCode: emp.address?.zipCode || '',
        country: emp.address?.country || ''
      },

      emergencyContact: {
        name: emp.emergencyContact?.name || '',
        relationship: emp.emergencyContact?.relationship || '',
        phone: emp.emergencyContact?.phone || '',
        email: emp.emergencyContact?.email || ''
      },

      skills: emp.skills || [],
      qualifications: emp.qualifications ? emp.qualifications.map(q => (typeof q === 'string' ? q : q.name || q.title)) : [],
      certifications: emp.certifications ? emp.certifications.map(c => (typeof c === 'string' ? c : c.name || c.title)) : [],
      experience: emp.experience || '',
      previousEmployer: emp.previousEmployer || '',
      references: emp.references || []
    });

    console.log('Form populated with employee data (RTK Query):', emp);
    console.log('Salary data from employee:', emp.salary);
    console.log('Tax info from employee:', emp.taxInfo);
    console.log('TDS declaration from employee:', emp.taxInfo?.tdsDeclaration);
    console.log('Date fields from employee:', {
      dateOfBirth: emp.dateOfBirth,
      hireDate: emp.hireDate,
      joiningDate: emp.joiningDate,
      dateOfJoining: emp.dateOfJoining
    });
    console.log('Formatted date fields:', {
      dateOfBirth: formatDateForInput(emp.dateOfBirth),
      hireDate: formatDateForInput(emp.hireDate || emp.dateOfJoining),
      joiningDate: formatDateForInput(emp.joiningDate || emp.dateOfJoining)
    });

    // Also log the final formData state after setting
    setTimeout(() => {
      console.log('Final formData state:', formData);
    }, 100);
  }, [isEditing, employeeResponse, currentEmployee]);

  // Auto-populate employee ID for new employees
  useEffect(() => {
    if (!isEditing && generatedIdResponse?.success && generatedIdResponse.data?.employeeId) {
      setFormData(prev => ({
        ...prev,
        employeeId: generatedIdResponse.data.employeeId
      }));
    }
  }, [isEditing, generatedIdResponse]);

  // Debug: Monitor formData changes (only for date fields in edit mode)
  useEffect(() => {
    if (isEditing) {
      console.log('Date fields in formData:', {
        dateOfBirth: formData.dateOfBirth,
        hireDate: formData.hireDate,
        joiningDate: formData.joiningDate
      });
    }
  }, [formData.dateOfBirth, formData.hireDate, formData.joiningDate, isEditing]);

  useEffect(() => {
    if (isEditing && documentsResponse.data && Array.isArray(documentsResponse.data)) {
      setEmployeeDocuments(documentsResponse.data);
    }
  }, [isEditing, documentsResponse.data]);

  // Handle salary display for edit mode
  useEffect(() => {
    if (isEditing && formData.salary && typeof formData.salary === 'object' && formData.salary.gross) {
      console.log('Editing employee with salary object:', formData.salary);
      // If salary is an object with breakdown, display the gross value
      setFormData(prev => ({
        ...prev,
        salary: formData.salary.gross.toString()
      }));
      // Set the salary breakdown for display
      setSalaryBreakdown(formData.salary);
      console.log('Salary breakdown set for editing:', formData.salary);
    } else if (isEditing && formData.salary && typeof formData.salary === 'number') {
      console.log('Editing employee with salary number:', formData.salary);
      // If salary is a number, convert to string for input field
      setFormData(prev => ({
        ...prev,
        salary: formData.salary.toString()
      }));
    }
  }, [isEditing, formData.salary]);

  // Function to calculate salary breakdown
  const calculateSalaryBreakdown = async (grossSalary) => {
    if (!grossSalary || grossSalary <= 0) {
      setSalaryBreakdown(null);
      return;
    }

    setIsCalculatingSalary(true);
    try {
      const result = await calculateBreakdown({
        grossSalary: parseFloat(grossSalary),
        companyId: user?.companyId
      }).unwrap();

      // Extract the breakdown from the API response
      setSalaryBreakdown(result.data?.breakdown);
      console.log('Salary breakdown calculated:', result.breakdown);
    } catch (error) {
      console.error('Error calculating salary breakdown:', error);
      setSalaryBreakdown(null);
    } finally {
      setIsCalculatingSalary(false);
    }
  };

  // Auto-calculate salary breakdown when gross salary changes
  useEffect(() => {
    if (formData.salary && !isCalculatingSalary) {
      const timeoutId = setTimeout(() => {
        calculateSalaryBreakdown(formData.salary);
      }, 500); // Debounce for 500ms

      return () => clearTimeout(timeoutId);
    }
  }, [formData.salary, user?.companyId]);

  const handleDocumentsChange = (newDocuments) => {
    // Ensure newDocuments is always an array
    const safeDocuments = Array.isArray(newDocuments) ? newDocuments : [];
    setEmployeeDocuments(safeDocuments);
  };

  const validateForm = () => {
    const newErrors = {};
    console.log('Starting form validation...');

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
  if (!formData.phone || formData.phone.trim() === '') newErrors.phone = 'Phone number is required';
  if (!formData.dateOfBirth || formData.dateOfBirth.trim() === '') newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.department) newErrors.department = 'Department is required';
    if (!formData.designation) newErrors.designation = 'Designation is required';
    if (!formData.branch) newErrors.branch = 'Branch is required';
    if (!formData.hireDate) {
      newErrors.hireDate = 'Hire date is required';
    } else {
      // Validate date format
      const hireDateObj = new Date(formData.hireDate);
      if (isNaN(hireDateObj.getTime())) {
        newErrors.hireDate = 'Please enter a valid date';
      } else if (hireDateObj > new Date()) {
        newErrors.hireDate = 'Hire date cannot be in the future';
      }
    }

    console.log('Validation errors:', newErrors);
    console.log('Validation result:', Object.keys(newErrors).length === 0);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');
    console.log('Form data:', formData);

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    console.log('Form validation passed, proceeding with submission');
    setErrors({}); // Clear any previous errors
    setSuccessMessage(''); // Clear any previous success message

    try {
      // Prepare data for submission
      const submissionData = {
        ...formData,
        // Use calculated salary breakdown if available, otherwise use simple salary
        salary: salaryBreakdown ? salaryBreakdown : (formData.salary ? parseFloat(formData.salary) : undefined),
        hourlyRate: formData.hourlyRate ? parseFloat(formData.hourlyRate) : undefined,
        probationPeriod: formData.probationPeriod ? parseInt(formData.probationPeriod) : undefined,
        taxInfo: {
          pan: formData.taxInfo.pan,
          taxRegime: formData.taxInfo.taxRegime,
          pfApplicable: formData.taxInfo.pfApplicable,
          pfNumber: formData.taxInfo.pfNumber,
          esiNumber: formData.taxInfo.esiNumber,
          uanNumber: formData.taxInfo.uanNumber,
          ptApplicable: formData.taxInfo.ptApplicable,
          esicApplicable: formData.taxInfo.esicApplicable,
          tdsApplicable: formData.taxInfo.tdsApplicable,
          tdsDeclaration: formData.taxInfo.tdsDeclaration ? {
            ...formData.taxInfo.tdsDeclaration,
            declarationDate: formData.taxInfo.tdsDeclaration.isDeclared ? new Date() : undefined,
            status: formData.taxInfo.tdsDeclaration.isDeclared ? 'pending' : 'pending' // New declarations start as pending
          } : {
            isDeclared: false,
            declarationType: 'no_tds',
            reason: 'no_income',
            remarks: ''
          }
        },
        // Transform bank details to proper structure
        bankDetails: (formData.bankName || formData.accountNumber || formData.ifscCode) ? {
          accountNumber: formData.accountNumber || '',
          bankName: formData.bankName || '',
          branchName: formData.bankBranch || '',
          ifscCode: formData.ifscCode || '',
          accountHolderName: formData.accountHolderName || `${formData.firstName} ${formData.lastName}`.trim()
        } : undefined
      };

      // Remove individual bank fields since they're now in bankDetails
      delete submissionData.bankName;
      delete submissionData.accountNumber;
      delete submissionData.ifscCode;
      delete submissionData.bankBranch;
      delete submissionData.accountHolderName;

      // Remove tax fields from root level as they're now in taxInfo
      delete submissionData.pan;
      delete submissionData.taxRegime;
      delete submissionData.pfNumber;
      delete submissionData.esiNumber;
      delete submissionData.uanNumber;
      delete submissionData.tdsApplicable;
      delete submissionData.tdsDeclaration;

      // Remove empty employeeId for new employees (let backend generate it)
      if (!isEditing && (!submissionData.employeeId || submissionData.employeeId.trim() === '')) {
        delete submissionData.employeeId;
      }

      console.log('Final submission data:', submissionData);

      console.log('Final submission data:', submissionData);

      let result;
      if (isEditing) {
        console.log('Making update API call for employee:', id);
        console.log('Update API URL would be:', `/employees/${id}`);
        result = await updateEmployeeMutation({ id, ...submissionData });
        console.log('Update API result:', result);
        // RTK Query returns { data } on success and { error } on failure
        if (result.error) {
          console.log('Update API returned error:', result.error);
          throw result.error;
        }
        // Some APIs may return success:false inside a 2xx response
        if (result.data && result.data.success === false) {
          console.log('Update API returned success:false:', result.data);
          const err = new Error(result.data.message || 'Validation failed');
          err.status = 400;
          err.data = result.data;
          throw err;
        }
        setSuccessMessage(result.data?.message || 'Employee updated successfully!');
      } else {
        console.log('Making create API call');
        console.log('Create API URL would be:', '/employees');
        result = await createEmployeeMutation(submissionData);
        console.log('Create API result:', result);
        if (result.error) {
          console.log('Create API returned error:', result.error);
          throw result.error;
        }
        if (result.data && result.data.success === false) {
          console.log('Create API returned success:false:', result.data);
          const err = new Error(result.data.message || 'Validation failed');
          err.status = 400;
          err.data = result.data;
          throw err;
        }
        setSuccessMessage(result.data?.message || 'Employee created successfully!');
      }

      console.log('Employee saved successfully:', result.data || result);

      // Additional logging for debugging
      if (result.payload) {
        console.log('Success payload:', result.payload);
      } else {
        console.log('Success result:', result);
      }

      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate('/employees');
      }, 1500);
    } catch (error) {
      console.error('Error saving employee:', error);
      console.error('Error details:', {
        message: error?.message,
        status: error?.status,
        data: error?.data,
        fullError: error
      });

      // Handle RTK Query errors - check for different error structures
      let errorMessage = 'An unexpected error occurred. Please try again.';
      let errorData = null;
      let errorStatus = null;

      // RTK Query error structure
      if (error?.error) {
        // RTK Query wraps the actual error in error.error
        const rtkError = error.error;
        console.error('RTK Query error:', rtkError);
        errorStatus = rtkError?.status;
        errorData = rtkError?.data;
        errorMessage = rtkError?.data?.message || rtkError?.message || errorMessage;
      } else {
        // Direct error
        errorStatus = error?.status;
        errorData = error?.data;
        errorMessage = error?.data?.message || error?.message || errorMessage;
      }

      console.error('Processed error:', { errorMessage, errorStatus, errorData });

      // Handle different error types
      if (errorStatus === 400 && errorData?.errors) {
        // Validation errors from backend
        const backendErrors = {};
        Object.keys(errorData.errors).forEach(field => {
          // Map backend field names to frontend field names if needed
          const frontendField = mapBackendFieldToFrontend(field);
          backendErrors[frontendField] = Array.isArray(errorData.errors[field])
            ? errorData.errors[field].join(', ')
            : errorData.errors[field];
        });
        setErrors(backendErrors);
      } else if (errorStatus === 400 && errorData?.message) {
        // General validation error
        setErrors({ general: errorData.message });
      } else if (errorStatus >= 500) {
        // Server error
        setErrors({ general: 'Server error occurred. Please try again later.' });
      } else if (errorData?.message) {
        // Other backend error with message
        setErrors({ general: errorData.message });
      } else if (errorMessage && errorMessage !== 'An unexpected error occurred. Please try again.') {
        // Use the extracted error message
        setErrors({ general: errorMessage });
      } else {
        // Fallback error message
        setErrors({ general: 'An unexpected error occurred. Please try again.' });
      }

      // Ensure error is always displayed - additional safeguard
      if (!errors.general && !Object.keys(errors).some(key => key !== 'general')) {
        setErrors({ general: errorMessage || 'An error occurred while saving the employee. Please check the console for details.' });
      }
    }
  };

  // Helper function to map backend field names to frontend field names
  const mapBackendFieldToFrontend = (backendField) => {
    const fieldMapping = {
      'dateOfJoining': 'hireDate',
      'firstName': 'firstName',
      'lastName': 'lastName',
      'email': 'email',
      'phone': 'phone',
      'employeeId': 'employeeId',
      'department': 'department',
      'designation': 'designation',
      'branch': 'branch'
    };
    return fieldMapping[backendField] || backendField;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    // Clear general error when user starts making changes
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleArrayInputChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: array
    }));
  };

  const handleTextareaChange = (field, value) => {
    const array = value.split('\n').map(item => item.trim()).filter(item => item);
    setFormData(prev => ({
      ...prev,
      [field]: array
    }));
  };

  // Skills tag input handlers
  const addSkillTag = (skill) => {
    const trimmedSkill = skill.trim();
    if (trimmedSkill && !formData.skills.includes(trimmedSkill)) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, trimmedSkill]
      }));
      setSkillInputValue('');
    }
  };

  const removeSkillTag = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  // Qualifications handlers
  const addQualification = (qualification) => {
    if (qualification && !formData.qualifications.includes(qualification)) {
      setFormData(prev => ({
        ...prev,
        qualifications: [...prev.qualifications, qualification],
        qualificationInput: '' // Clear input
      }));
    }
  };

  const removeQualification = (qualificationToRemove) => {
    setFormData(prev => ({
      ...prev,
      qualifications: prev.qualifications.filter(qualification => qualification !== qualificationToRemove)
    }));
  };

  // Certifications handlers
  const addCertification = (certification) => {
    if (certification && !formData.certifications.includes(certification)) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, certification],
        certificationInput: '' // Clear input
      }));
    }
  };

  const removeCertification = (certificationToRemove) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(certification => certification !== certificationToRemove)
    }));
  };

  const handleSkillInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addSkillTag(skillInputValue);
    } else if (e.key === 'Backspace' && skillInputValue === '' && formData.skills.length > 0) {
      removeSkillTag(formData.skills[formData.skills.length - 1]);
    }
  };

  const tabs = [
    { id: 'basic', label: t('employee:basic_info'), icon: UserIcon },
    { id: 'employment', label: t('employee:employment_details'), icon: BriefcaseIcon },
    { id: 'compensation', label: t('employee:compensation'), icon: CurrencyDollarIcon },
    { id: 'address', label: t('employee:address'), icon: MapPinIcon },
    { id: 'emergency', label: t('employee:emergency_contact'), icon: HeartIcon },
    { id: 'additional', label: t('employee:additional_info'), icon: DocumentIcon },
    { id: 'tax', label: 'Tax Declaration', icon: DocumentIcon },
    ...(isEditing ? [{ id: 'documents', label: 'Documents', icon: PhotoIcon }] : [])
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">Loading employee form...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? t('employee:edit_employee') : t('employee:add_employee')} - IAR HRMS</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Modern Header */}
        <div className="bg-white/80 backdrop-blur-sm border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
                <button
                  onClick={() => navigate('/employees')}
                  className="flex items-center px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-900 bg-white/50 rounded-lg sm:rounded-xl hover:bg-white/80 transition-all duration-200 group flex-shrink-0"
                >
                  <ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
                  <span className="hidden xs:inline">{t('common:back')}</span>
                </button>
                <div className="min-w-0 flex-1">
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent truncate">
                    {isEditing ? t('employee:edit_employee') : t('employee:add_employee')}
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">
                    {isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 ml-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/employees')}
                  className="px-3 sm:px-6 py-2 sm:py-3 border border-gray-300 text-gray-700 rounded-lg sm:rounded-xl hover:bg-white/80 hover:border-gray-400 transition-all duration-200 transform hover:scale-105 text-sm"
                >
                  <span className="hidden sm:inline">{t('common:cancel')}</span>
                  <span className="sm:hidden">Cancel</span>
                </button>
                {!isEditing && (
                  <button
                    type="button"
                    onClick={initializeWithDummyData}
                    className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg sm:rounded-xl hover:from-orange-600 hover:to-red-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center relative group"
                    title="Fill form with realistic test data for development and testing"
                  >
                    <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                    <span className="hidden md:inline">Fill with Dummy Data</span>
                    <span className="md:hidden">Dummy</span>
                    <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                      Generate realistic employee data for testing
                    </div>
                  </button>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={isCreating || isUpdating}
                  className="px-3 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg sm:rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center text-sm"
                >
                  {(isCreating || isUpdating) ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-2 border-white border-t-transparent mr-1 sm:mr-2"></div>
                      <span className="hidden sm:inline">{t('common:save')}</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">{t('common:save')}</span>
                      <span className="sm:hidden">Save</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
          {/* Compact Modern Progress Tabs */}
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-white/60 p-4 mb-6 shadow-xl relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-purple-50/20 to-indigo-50/30"></div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-200/15 to-transparent rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-purple-200/15 to-transparent rounded-full translate-y-8 -translate-x-8"></div>

            <div className="relative z-10">
              {/* Compact Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-semibold text-gray-700">Progress</h2>
                  <span className="text-xs font-medium text-gray-500">
                    {tabs.findIndex(tab => tab.id === activeTab) + 1}/{tabs.length}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${((tabs.findIndex(tab => tab.id === activeTab) + 1) / tabs.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Compact Tabs Container */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {tabs.map((tab, index) => {
                  const isActive = activeTab === tab.id;
                  const isCompleted = tabs.findIndex(t => t.id === activeTab) > index;
                  const isNext = tabs.findIndex(t => t.id === activeTab) + 1 === index;

                  return (
                    <React.Fragment key={tab.id}>
                      {/* Compact Tab Button */}
                      <button
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                          group relative flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg sm:rounded-xl font-medium text-xs transition-all duration-300 transform hover:scale-105
                          ${isActive
                            ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white shadow-lg shadow-blue-500/25'
                            : isCompleted
                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md shadow-green-500/25'
                            : 'bg-white/70 text-gray-600 hover:bg-white hover:shadow-md hover:shadow-gray-200/50 border border-gray-200/50'
                          }
                          ${isNext ? 'ring-1 ring-blue-300 ring-opacity-50' : ''}
                        `}
                      >
                        {/* Compact Step Number */}
                        <div className={`
                          flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full mr-1.5 sm:mr-2 text-xs font-bold transition-all duration-300
                          ${isActive
                            ? 'bg-white/20 text-white'
                            : isCompleted
                            ? 'bg-white/20 text-white'
                            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                          }
                        `}>
                          {isCompleted ? (
                            <CheckCircleIcon className="w-3 h-3" />
                          ) : (
                            index + 1
                          )}
                        </div>

                        {/* Compact Icon */}
                        <tab.icon className={`
                          w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2 transition-all duration-300
                          ${isActive || isCompleted ? 'text-white' : 'text-blue-600 group-hover:text-purple-600'}
                        `} />

                        {/* Compact Label */}
                        <span className="whitespace-nowrap hidden xs:inline sm:inline">{tab.label}</span>
                        <span className="whitespace-nowrap xs:hidden sm:hidden">{tab.label.split(' ')[0]}</span>

                        {/* Active Indicator */}
                        {isActive && (
                          <div className="absolute -bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 sm:w-1.5 h-1 sm:h-1.5 bg-white rounded-full animate-pulse"></div>
                        )}

                        {/* Hover Effect */}
                        <div className={`
                          absolute inset-0 rounded-lg sm:rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100
                          ${isActive
                            ? 'bg-white/10'
                            : 'bg-gradient-to-r from-blue-500/10 to-purple-500/10'
                          }
                        `}></div>
                      </button>

                      {/* Compact Connector Line */}
                      {index < tabs.length - 1 && (
                        <div className="hidden md:flex items-center mx-0.5 sm:mx-1">
                          <div className={`
                            w-3 sm:w-4 h-0.5 transition-all duration-500 rounded-full
                            ${isCompleted || isActive
                              ? 'bg-gradient-to-r from-green-400 to-blue-400'
                              : 'bg-gray-300'
                            }
                          `}></div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Compact Current Tab Info */}
              <div className="mt-3 text-center">
                <div className="inline-flex items-center px-3 py-1 bg-white/50 backdrop-blur-sm rounded-lg border border-white/30">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-xs font-medium text-gray-600">
                    <span className="font-semibold text-blue-600">
                      {tabs.find(tab => tab.id === activeTab)?.label}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
            {/* General Error Display */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-red-700 font-medium">{errors.general}</p>
                </div>
              </div>
            )}

            {/* Success Message Display */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 rounded-lg sm:rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 sm:mr-3 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-green-700 font-medium">{successMessage}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            {activeTab === 'basic' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
                    <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
                    {t('employee:basic_info')}
                  </h3>
                  <p className="text-blue-100 mt-1 text-sm">Personal and contact information</p>
                </div>
                <div className="p-4 sm:p-8">
                  <div className="grid grid-cols-1 gap-4 sm:gap-8 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="employeeId" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:employee_id')}
                        {!isEditing && (
                          <span className="text-green-600 text-xs ml-2">
                            (Auto-generated)
                          </span>
                        )}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          id="employeeId"
                          value={formData.employeeId}
                          onChange={(e) => handleInputChange('employeeId', e.target.value)}
                          disabled={isCreating || isUpdating}
                          className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          placeholder="EMP001"
                        />
                        {!isEditing && (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const result = await refetchEmployeeId();
                                if (result.data?.success && result.data.data?.employeeId) {
                                  setFormData(prev => ({
                                    ...prev,
                                    employeeId: result.data.data.employeeId
                                  }));
                                }
                              } catch (error) {
                                console.error('Failed to regenerate employee ID:', error);
                              }
                            }}
                            disabled={isCreating || isUpdating || generatingId}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium"
                            title="Regenerate Employee ID"
                          >
                            {generatingId ? '⏳' : '🔄'}
                          </button>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="firstName" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:first_name')} *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        required
                        disabled={isCreating || isUpdating}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                          errors.firstName ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      />
                      {errors.firstName && <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-600 font-medium">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:last_name')} *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        required
                        disabled={isCreating || isUpdating}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed text-sm ${
                          errors.lastName ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      />
                      {errors.lastName && <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-600 font-medium">{errors.lastName}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:email')} *
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white text-sm ${
                          errors.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      />
                      {errors.email && <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-600 font-medium">{errors.email}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:phone')}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="dateOfBirth" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:date_of_birth')}
                      </label>
                      <input
                        type="date"
                        id="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white text-sm"
                      />
                    </div>

                    <div>
                      <label htmlFor="gender" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:gender')}
                      </label>
                      <select
                        id="gender"
                        value={formData.gender}
                        onChange={(e) => handleInputChange('gender', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white text-sm"
                      >
                        <option value="">{t('employee:select_gender')}</option>
                        <option value="male">{t('employee:male')}</option>
                        <option value="female">{t('employee:female')}</option>
                        <option value="other">{t('employee:other')}</option>
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="maritalStatus" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:marital_status')}
                      </label>
                      <select
                        id="maritalStatus"
                        value={formData.maritalStatus}
                        onChange={(e) => handleInputChange('maritalStatus', e.target.value)}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-200 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white text-sm"
                      >
                        <option value="">{t('employee:select_marital_status')}</option>
                        <option value="single">{t('employee:single')}</option>
                        <option value="married">{t('employee:married')}</option>
                        <option value="divorced">{t('employee:divorced')}</option>
                        <option value="widowed">{t('employee:widowed')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Employment Details */}
            {activeTab === 'employment' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <BriefcaseIcon className="h-6 w-6 mr-3" />
                    {t('employee:employment_details')}
                  </h3>
                  <p className="text-green-100 mt-1">Job position and employment information</p>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <div>
                      <label htmlFor="department" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:department')} *
                      </label>
                      <select
                        id="department"
                        value={formData.department}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                        required
                        disabled={departmentsLoading || departmentsError || departments.length === 0}
                        className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white ${
                          errors.department ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      >
                        <option value="">
                          {departmentsLoading
                            ? t('common:loading')
                            : departmentsError
                            ? t('common:error_loading')
                            : t('employee:select_department')
                          }
                        </option>
                        {Array.isArray(departments) && departments.map((dept) => (
                          <option key={dept._id} value={dept._id}>
                            {dept.name}
                          </option>
                        ))}
                      </select>
                      {errors.department && <p className="mt-2 text-sm text-red-600 font-medium">{errors.department}</p>}
                    </div>

                    <div>
                      <label htmlFor="branch" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:branch')} *
                      </label>
                      <select
                        id="branch"
                        value={formData.branch}
                        onChange={(e) => handleInputChange('branch', e.target.value)}
                        required
                        disabled={branchesLoading || branches.length === 0}
                        className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white ${
                          errors.branch ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      >
                        <option value="">
                          {branchesLoading
                            ? t('common:loading')
                            : branches.length === 0
                            ? t('common:noDataFound')
                            : t('employee:select_branch')
                          }
                        </option>
                        {Array.isArray(branches) && branches.map((branch) => (
                          <option key={branch._id} value={branch._id}>
                            {branch.name}
                          </option>
                        ))}
                      </select>
                      {errors.branch && <p className="mt-2 text-sm text-red-600 font-medium">{errors.branch}</p>}
                    </div>

                    <div>
                      <label htmlFor="designation" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:designation')} *
                      </label>
                      <select
                        id="designation"
                        value={formData.designation}
                        onChange={(e) => handleInputChange('designation', e.target.value)}
                        required
                        disabled={designationsLoading || designationsError || designations.length === 0}
                        className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white ${
                          errors.designation ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      >
                        <option value="">
                          {designationsLoading
                            ? t('common:loading')
                            : designationsError
                            ? t('common:error_loading')
                            : t('employee:select_designation')
                          }
                        </option>
                        {Array.isArray(designations) && designations.map((desig) => (
                          <option key={desig._id} value={desig._id}>
                            {desig.name}
                          </option>
                        ))}
                      </select>
                      {errors.designation && <p className="mt-2 text-sm text-red-600 font-medium">{errors.designation}</p>}
                    </div>

                    <div>
                      <label htmlFor="shiftId" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('common:shift')}
                      </label>
                      <select
                        id="shiftId"
                        value={formData.shiftId}
                        onChange={(e) => handleInputChange('shiftId', e.target.value)}
                        disabled={shiftsLoading || shifts.length === 0}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="">
                          {shiftsLoading
                            ? t('common:loading')
                            : shifts.length === 0
                            ? t('common:noDataFound')
                            : t('employee:select_shift')
                          }
                        </option>
                        {Array.isArray(shifts) && shifts.map((shift) => (
                          <option key={shift._id} value={shift._id}>
                            {shift.name} ({shift.startTime} - {shift.endTime})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="employmentType" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:employment_type')}
                      </label>
                      <select
                        id="employmentType"
                        value={formData.employmentType}
                        onChange={(e) => handleInputChange('employmentType', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="full-time">{t('employee:full_time')}</option>
                        <option value="part-time">{t('employee:part_time')}</option>
                        <option value="contract">{t('employee:contract')}</option>
                        <option value="intern">{t('employee:intern')}</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="manager" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:manager')}
                      </label>
                      <select
                        id="manager"
                        value={formData.manager}
                        onChange={(e) => handleInputChange('manager', e.target.value)}
                        disabled={usersLoading}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="">
                          {usersLoading
                            ? 'Loading users...'
                            : 'Select Manager (Optional)'
                          }
                        </option>
                        {users && users.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.fullName} ({user.role})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="workLocation" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:work_location')}
                      </label>
                      <select
                        id="workLocation"
                        value={formData.workLocation}
                        onChange={(e) => handleInputChange('workLocation', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="">Select Work Location</option>
                        <option value="Office">Office</option>
                        <option value="Remote">Remote</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="hireDate" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:hire_date')} *
                      </label>
                      <input
                        type="date"
                        id="hireDate"
                        value={formData.hireDate}
                        onChange={(e) => handleInputChange('hireDate', e.target.value)}
                        required
                        disabled={isCreating || isUpdating}
                        className={`w-full px-4 py-3 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed ${
                          errors.hireDate ? 'border-red-300 focus:ring-red-500' : 'border-gray-200'
                        }`}
                      />
                      {errors.hireDate && <p className="mt-2 text-sm text-red-600 font-medium">{errors.hireDate}</p>}
                    </div>

                    <div>
                      <label htmlFor="joiningDate" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:joining_date')}
                      </label>
                      <input
                        type="date"
                        id="joiningDate"
                        value={formData.joiningDate}
                        onChange={(e) => handleInputChange('joiningDate', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="probationPeriod" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:probation_period_months')}
                      </label>
                      <select
                        id="probationPeriod"
                        value={formData.probationPeriod}
                        onChange={(e) => handleInputChange('probationPeriod', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="3">3 {t('employee:months')}</option>
                        <option value="6">6 {t('employee:months')}</option>
                        <option value="12">12 {t('employee:months')}</option>
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="status" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:status')}
                      </label>
                      <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      >
                        <option value="active">{t('employee:active')}</option>
                        <option value="inactive">{t('employee:inactive')}</option>
                        <option value="on-leave">{t('employee:on_leave')}</option>
                        <option value="terminated">{t('employee:terminated')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Compensation */}
            {activeTab === 'compensation' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-yellow-600 to-orange-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <CurrencyDollarIcon className="h-6 w-6 mr-3" />
                    {t('employee:compensation')}
                  </h3>
                  <p className="text-yellow-100 mt-1">Salary structure and payment information</p>
                </div>
                <div className="p-8">
                  <div className="space-y-6">
                    {/* Gross Salary */}
                    <div>
                      <label htmlFor="grossSalary" className="block text-sm font-semibold text-gray-700 mb-2">
                        Gross Salary *
                      </label>
                      <input
                        type="number"
                        id="grossSalary"
                        value={formData.salary}
                        onChange={(e) => handleInputChange('salary', e.target.value)}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                        placeholder="70000"
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        Enter the gross monthly salary. CTC will be calculated automatically including employer contributions.
                      </p>
                    </div>

                    {/* Salary Breakdown Preview */}
                    {formData.salary && (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-green-800 flex items-center">
                            <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                            Salary Breakdown Preview
                          </h4>
                          {isCalculatingSalary && (
                            <div className="flex items-center text-sm text-green-600">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                              Calculating...
                            </div>
                          )}
                        </div>

                        {salaryBreakdown ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Earnings */}
                            <div className="bg-white/70 rounded-lg p-4">
                              <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                                Earnings
                              </h5>
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Basic Salary:</span>
                                  <span className="font-medium text-green-700">₹{salaryBreakdown.basic?.toLocaleString() || 0}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">HRA:</span>
                                  <span className="font-medium text-green-700">₹{salaryBreakdown.hra?.toLocaleString() || 0}</span>
                                </div>
                                {salaryBreakdown.allowances && salaryBreakdown.allowances.map((allowance, index) => (
                                  <div key={index} className="flex justify-between text-sm">
                                    <span className="text-gray-600">{allowance.name}:</span>
                                    <span className="font-medium text-green-700">₹{allowance.amount?.toLocaleString() || 0}</span>
                                  </div>
                                ))}
                                <div className="border-t border-gray-200 pt-2 mt-3">
                                  <div className="flex justify-between text-sm font-semibold">
                                    <span className="text-gray-700">Gross Salary:</span>
                                    <span className="text-green-800">₹{salaryBreakdown.gross?.toLocaleString() || 0}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Deductions */}
                            <div className="bg-white/70 rounded-lg p-4">
                              <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                                Deductions
                              </h5>
                              <div className="space-y-2">
                                {salaryBreakdown.deductions?.pf?.employee > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">PF (Employee):</span>
                                    <span className="font-medium text-red-600">₹{salaryBreakdown.deductions.pf.employee?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                {salaryBreakdown.deductions?.esi?.employee > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">ESI (Employee):</span>
                                    <span className="font-medium text-red-600">₹{salaryBreakdown.deductions.esi.employee?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                {salaryBreakdown.deductions?.professionalTax > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Professional Tax:</span>
                                    <span className="font-medium text-red-600">₹{salaryBreakdown.deductions.professionalTax?.toLocaleString() || 0}</span>
                                  </div>
                                )}
                                <div className="border-t border-gray-200 pt-2 mt-3">
                                  <div className="flex justify-between text-sm font-semibold">
                                    <span className="text-gray-700">Net Salary:</span>
                                    <span className="text-blue-800">₹{salaryBreakdown.netSalary?.toLocaleString() || 0}</span>
                                  </div>
                                  <div className="flex justify-between text-sm font-semibold mt-1">
                                    <span className="text-gray-700">CTC:</span>
                                    <span className="text-purple-800">₹{salaryBreakdown.ctc?.toLocaleString() || 0}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8">
                            <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500">Enter a gross salary to see the breakdown</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Bank Details */}
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="bankName" className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('employee:bank_name')}
                        </label>
                        <input
                          type="text"
                          id="bankName"
                          value={formData.bankName}
                          onChange={(e) => handleInputChange('bankName', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                          placeholder="HDFC Bank"
                        />
                      </div>

                      <div>
                        <label htmlFor="accountNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('employee:account_number')}
                        </label>
                        <input
                          type="text"
                          id="accountNumber"
                          value={formData.accountNumber}
                          onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                          placeholder="1234567890"
                        />
                      </div>

                      <div>
                        <label htmlFor="ifscCode" className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('employee:ifsc_code')}
                        </label>
                        <input
                          type="text"
                          id="ifscCode"
                          value={formData.ifscCode}
                          onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                          placeholder="HDFC0001234"
                        />
                      </div>

                      <div>
                        <label htmlFor="bankBranch" className="block text-sm font-semibold text-gray-700 mb-2">
                          Bank Branch
                        </label>
                        <input
                          type="text"
                          id="bankBranch"
                          value={formData.bankBranch}
                          onChange={(e) => handleInputChange('bankBranch', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                          placeholder="Main Branch"
                        />
                      </div>

                      <div>
                        <label htmlFor="accountHolderName" className="block text-sm font-semibold text-gray-700 mb-2">
                          Account Holder Name
                        </label>
                        <input
                          type="text"
                          id="accountHolderName"
                          value={formData.accountHolderName}
                          onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label htmlFor="payFrequency" className="block text-sm font-semibold text-gray-700 mb-2">
                          {t('employee:pay_frequency')}
                        </label>
                        <select
                          id="payFrequency"
                          value={formData.payFrequency}
                          onChange={(e) => handleInputChange('payFrequency', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                        >
                          <option value="monthly">{t('employee:monthly')}</option>
                          <option value="weekly">{t('employee:weekly')}</option>
                          <option value="bi-weekly">{t('employee:bi_weekly')}</option>
                          <option value="annually">{t('employee:annually')}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Address */}
            {activeTab === 'address' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <MapPinIcon className="h-6 w-6 mr-3" />
                    {t('employee:address')}
                  </h3>
                  <p className="text-purple-100 mt-1">Residential and contact address</p>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="street" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:street_address')}
                      </label>
                      <input
                        type="text"
                        id="street"
                        value={formData.address.street}
                        onChange={(e) => handleNestedInputChange('address', 'street', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:city')}
                      </label>
                      <input
                        type="text"
                        id="city"
                        value={formData.address.city}
                        onChange={(e) => handleNestedInputChange('address', 'city', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:state')}
                      </label>
                      <input
                        type="text"
                        id="state"
                        value={formData.address.state}
                        onChange={(e) => handleNestedInputChange('address', 'state', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:zip_code')}
                      </label>
                      <input
                        type="text"
                        id="zipCode"
                        value={formData.address.zipCode}
                        onChange={(e) => handleNestedInputChange('address', 'zipCode', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="country" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:country')}
                      </label>
                      <input
                        type="text"
                        id="country"
                        value={formData.address.country}
                        onChange={(e) => handleNestedInputChange('address', 'country', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                        placeholder="United States"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Emergency Contact */}
            {activeTab === 'emergency' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-red-600 to-rose-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <HeartIcon className="h-6 w-6 mr-3" />
                    {t('employee:emergency_contact')}
                  </h3>
                  <p className="text-red-100 mt-1">Emergency contact information</p>
                </div>
                <div className="p-8">
                  <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
                    <div>
                      <label htmlFor="emergencyName" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:emergency_contact_name')}
                      </label>
                      <input
                        type="text"
                        id="emergencyName"
                        value={formData.emergencyContact.name}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'name', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="emergencyRelationship" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:relationship')}
                      </label>
                      <input
                        type="text"
                        id="emergencyRelationship"
                        value={formData.emergencyContact.relationship}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'relationship', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                        placeholder="Spouse, Parent, Sibling"
                      />
                    </div>

                    <div>
                      <label htmlFor="emergencyPhone" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:emergency_phone')}
                      </label>
                      <input
                        type="tel"
                        id="emergencyPhone"
                        value={formData.emergencyContact.phone}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'phone', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="emergencyEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:emergency_email')}
                      </label>
                      <input
                        type="email"
                        id="emergencyEmail"
                        value={formData.emergencyContact.email}
                        onChange={(e) => handleNestedInputChange('emergencyContact', 'email', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Information */}
            {activeTab === 'additional' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <DocumentIcon className="h-6 w-6 mr-3" />
                    {t('employee:additional_info')}
                  </h3>
                  <p className="text-indigo-100 mt-1">Skills, qualifications, and additional details</p>
                </div>
                <div className="p-8">
                  <div className="space-y-8">
                    <div>
                      <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:years_of_experience')}
                      </label>
                      <input
                        type="number"
                        id="experience"
                        value={formData.experience}
                        onChange={(e) => handleInputChange('experience', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                        placeholder="5"
                      />
                    </div>

                    <div>
                      <label htmlFor="previousEmployer" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:previous_employer')}
                      </label>
                      <input
                        type="text"
                        id="previousEmployer"
                        value={formData.previousEmployer}
                        onChange={(e) => handleInputChange('previousEmployer', e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      />
                    </div>

                    <div>
                      <label htmlFor="skills" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:skills')}
                      </label>
                      <div className="border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-transparent bg-gray-50/50 hover:bg-white px-4 py-3">
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.skills.map((skill, index) => (
                            <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800 border border-indigo-200">
                              {skill}
                              <button
                                type="button"
                                onClick={() => removeSkillTag(skill)}
                                className="ml-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                                aria-label={`Remove ${skill}`}
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          id="skills"
                          value={skillInputValue}
                          onChange={(e) => setSkillInputValue(e.target.value)}
                          onKeyDown={handleSkillInputKeyDown}
                          className="w-full outline-none bg-transparent placeholder-gray-400"
                          placeholder={formData.skills.length === 0 ? "Type skills and press Enter, Space, or Comma to add" : "Add more skills..."}
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Press Enter, Space, or Comma to add a skill. Press Backspace on empty input to remove last tag.
                      </p>
                    </div>

                    {/* Qualifications Section */}
                    <div>
                      <label htmlFor="qualifications" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:qualifications')}
                      </label>
                      <div className="space-y-3">
                        {/* Add new qualification input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            id="qualification-input"
                            value={formData.qualificationInput || ''}
                            onChange={(e) => handleInputChange('qualificationInput', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && formData.qualificationInput?.trim()) {
                                e.preventDefault();
                                addQualification(formData.qualificationInput.trim());
                              }
                            }}
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                            placeholder="Add a qualification..."
                          />
                          <button
                            type="button"
                            onClick={() => formData.qualificationInput?.trim() && addQualification(formData.qualificationInput.trim())}
                            disabled={!formData.qualificationInput?.trim()}
                            className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium"
                          >
                            Add
                          </button>
                        </div>

                        {/* Display qualifications */}
                        {formData.qualifications.length > 0 && (
                          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50 max-h-32 overflow-y-auto">
                            <div className="flex flex-wrap gap-2">
                              {formData.qualifications.map((qualification, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800 border border-indigo-200">
                                  {qualification}
                                  <button
                                    type="button"
                                    onClick={() => removeQualification(qualification)}
                                    className="ml-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                                    aria-label={`Remove ${qualification}`}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Certifications Section */}
                    <div>
                      <label htmlFor="certifications" className="block text-sm font-semibold text-gray-700 mb-2">
                        {t('employee:certifications')}
                      </label>
                      <div className="space-y-3">
                        {/* Add new certification input */}
                        <div className="flex gap-2">
                          <input
                            type="text"
                            id="certification-input"
                            value={formData.certificationInput || ''}
                            onChange={(e) => handleInputChange('certificationInput', e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && formData.certificationInput?.trim()) {
                                e.preventDefault();
                                addCertification(formData.certificationInput.trim());
                              }
                            }}
                            className="flex-1 px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                            placeholder="Add a certification..."
                          />
                          <button
                            type="button"
                            onClick={() => formData.certificationInput?.trim() && addCertification(formData.certificationInput.trim())}
                            disabled={!formData.certificationInput?.trim()}
                            className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200 text-sm font-medium"
                          >
                            Add
                          </button>
                        </div>

                        {/* Display certifications */}
                        {formData.certifications.length > 0 && (
                          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50 max-h-32 overflow-y-auto">
                            <div className="flex flex-wrap gap-2">
                              {formData.certifications.map((certification, index) => (
                                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800 border border-indigo-200">
                                  {certification}
                                  <button
                                    type="button"
                                    onClick={() => removeCertification(certification)}
                                    className="ml-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-200 rounded-full p-0.5 transition-colors"
                                    aria-label={`Remove ${certification}`}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tax Declaration Tab */}
            {activeTab === 'tax' && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <DocumentIcon className="h-6 w-6 mr-3" />
                    Tax Declaration
                  </h3>
                  <p className="text-blue-100 mt-1">Manage tax information and TDS declarations</p>
                </div>
                <div className="p-8">
                  <div className="space-y-8">
                    {/* Tax Information */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Tax Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">PAN Number</label>
                          <input
                            type="text"
                            value={formData.taxInfo?.pan || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, taxInfo: { ...prev.taxInfo, pan: e.target.value.toUpperCase() } }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                            placeholder="AAAAA0000A"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Tax Regime</label>
                          <select
                            value={formData.taxInfo?.taxRegime || 'new'}
                            onChange={(e) => setFormData(prev => ({ ...prev, taxInfo: { ...prev.taxInfo, taxRegime: e.target.value } }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                          >
                            <option value="new">New Tax Regime</option>
                            <option value="old">Old Tax Regime</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">UAN Number</label>
                          <input
                            type="text"
                            value={formData.taxInfo?.uanNumber || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, taxInfo: { ...prev.taxInfo, uanNumber: e.target.value } }))}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                            placeholder="Enter UAN Number"
                          />
                        </div>
                      </div>
                    </div>

                    {/* TDS Declaration */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Statutory Deductions</h4>
                      <div className="space-y-6">
                        {/* PF Applicable */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Provident Fund (PF)</h5>
                              <p className="text-xs text-gray-600 mt-1">Employee contribution: 12% of basic salary</p>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.taxInfo?.pfApplicable === true}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  taxInfo: { ...prev.taxInfo, pfApplicable: e.target.checked }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm font-medium text-gray-700">
                                PF Applicable
                              </label>
                            </div>
                          </div>
                          {formData.taxInfo?.pfApplicable && (
                            <div className="mt-4 pt-3 border-t border-gray-100 space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">PF Number</label>
                                <input
                                  type="text"
                                  value={formData.taxInfo?.pfNumber || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, taxInfo: { ...prev.taxInfo, pfNumber: e.target.value } }))}
                                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                  placeholder="Enter PF Number"
                                />
                              </div>
                              <p className="text-xs text-green-700 bg-green-50 p-2 rounded">
                                ✓ PF deductions will be calculated and applied to payroll
                              </p>
                            </div>
                          )}
                        </div>

                        {/* ESIC Applicable */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Employee State Insurance (ESIC)</h5>
                              <p className="text-xs text-gray-600 mt-1">Employee contribution: 0.75% of gross salary (up to ₹21,000)</p>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.taxInfo?.esicApplicable === true}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  taxInfo: { ...prev.taxInfo, esicApplicable: e.target.checked }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm font-medium text-gray-700">
                                ESIC Applicable
                              </label>
                            </div>
                          </div>
                          {formData.taxInfo?.esicApplicable && (
                            <div className="mt-4 pt-3 border-t border-gray-100 space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">ESI Number</label>
                                <input
                                  type="text"
                                  value={formData.taxInfo?.esiNumber || ''}
                                  onChange={(e) => setFormData(prev => ({ ...prev, taxInfo: { ...prev.taxInfo, esiNumber: e.target.value } }))}
                                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                  placeholder="Enter ESI Number"
                                />
                              </div>
                              <p className="text-xs text-green-700 bg-green-50 p-2 rounded">
                                ✓ ESIC deductions will be calculated and applied to payroll
                              </p>
                            </div>
                          )}
                        </div>

                        {/* PT Applicable */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Professional Tax (PT)</h5>
                              <p className="text-xs text-gray-600 mt-1">State-specific professional tax (₹235/month for salary &gt; ₹21,000)</p>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.taxInfo?.ptApplicable === true}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  taxInfo: { ...prev.taxInfo, ptApplicable: e.target.checked }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm font-medium text-gray-700">
                                PT Applicable
                              </label>
                            </div>
                          </div>
                          {formData.taxInfo?.ptApplicable && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs text-green-700 bg-green-50 p-2 rounded">
                                ✓ Professional Tax deductions will be calculated and applied to payroll
                              </p>
                            </div>
                          )}
                        </div>

                        {/* TDS Applicable */}
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Tax Deducted at Source (TDS)</h5>
                              <p className="text-xs text-gray-600 mt-1">Income tax deduction as per IT rules</p>
                            </div>
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={formData.taxInfo?.tdsApplicable === true}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  taxInfo: { ...prev.taxInfo, tdsApplicable: e.target.checked }
                                }))}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                              <label className="ml-2 text-sm font-medium text-gray-700">
                                TDS Applicable
                              </label>
                            </div>
                          </div>
                          {formData.taxInfo?.tdsApplicable && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-xs text-green-700 bg-green-50 p-2 rounded">
                                ✓ TDS deductions will be calculated and applied to payroll
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* TDS Declaration - Only show if TDS is applicable */}
                    {formData.taxInfo?.tdsApplicable && (
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">TDS Declaration</h4>
                        <div className="space-y-6">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.taxInfo.tdsDeclaration?.isDeclared || false}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                taxInfo: {
                                  ...prev.taxInfo,
                                  tdsDeclaration: {
                                    ...prev.taxInfo.tdsDeclaration,
                                    isDeclared: e.target.checked
                                  }
                                }
                              }))}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label className="ml-2 text-sm font-medium text-gray-700">
                              I declare that I do not want TDS to be deducted from my salary
                            </label>
                          </div>

                          {/* TDS Declaration Details - Only show if declared */}
                          {formData.taxInfo.tdsDeclaration?.isDeclared && (
                            <div className="space-y-4 bg-white rounded-lg p-4 border border-gray-200">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Declaration Type</label>
                                <select
                                  value={formData.taxInfo.tdsDeclaration?.declarationType || 'no_tds'}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    taxInfo: {
                                      ...prev.taxInfo,
                                      tdsDeclaration: {
                                        ...prev.taxInfo.tdsDeclaration,
                                        declarationType: e.target.value
                                      }
                                    }
                                  }))}
                                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="no_tds">No TDS Deduction</option>
                                  <option value="lower_tds">Lower TDS Rate</option>
                                  <option value="higher_tds">Higher TDS Rate</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Declaration</label>
                                <select
                                  value={formData.taxInfo.tdsDeclaration?.reason || 'no_income'}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    taxInfo: {
                                      ...prev.taxInfo,
                                      tdsDeclaration: {
                                        ...prev.taxInfo.tdsDeclaration,
                                        reason: e.target.value
                                      }
                                    }
                                  }))}
                                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                                >
                                  <option value="no_income">No Other Income</option>
                                  <option value="below_threshold">Income Below Taxable Limit</option>
                                  <option value="other">Other Reason</option>
                                </select>
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                                <textarea
                                  value={formData.taxInfo.tdsDeclaration?.remarks || ''}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    taxInfo: {
                                      ...prev.taxInfo,
                                      tdsDeclaration: {
                                        ...prev.taxInfo.tdsDeclaration,
                                        remarks: e.target.value
                                      }
                                    }
                                  }))}
                                  rows={3}
                                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 resize-none"
                                  placeholder="Additional remarks for TDS declaration"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && isEditing && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/50 shadow-lg overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6">
                  <h3 className="text-xl font-bold text-white flex items-center">
                    <PhotoIcon className="h-6 w-6 mr-3" />
                    Employee Documents
                  </h3>
                  <p className="text-green-100 mt-1">Upload and manage employee documents</p>
                </div>
                <div className="p-8">
                  <DocumentUpload
                    employeeId={id}
                    documents={employeeDocuments}
                    onDocumentsChange={handleDocumentsChange}
                    required={false}
                  />
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </>
  );
};

export default EmployeeForm;
